// The "Carte Entrepreneur 101" path — the academy's home page at "/". Nine
// trainings, one card: each training a participant completes earns a stamp,
// and the page plays that out by stamping a 3D card as the visitor scrolls
// through the nine. See src/components/home/ for the page itself.
//
// This module is the part the page and the server share: the copy (French and
// Arabic, as designed — there is no English version), which course is which
// live training, and how a home-page registration becomes a lead. No Node or
// React imports, so both sides can load it.

import { z } from "zod";

export type E101Lang = "fr" | "ar";

/**
 * Leads for the full path — and for a course that has no training of its own
 * yet — are filed under this training. It is created on the first such lead
 * (see `leads.createFromHome`) and kept inactive: the path's landing page is
 * "/", so it must never be published at /training/<slug> as well.
 */
export const PARCOURS_SLUG = "entrepreneur-101";
export const PARCOURS_NAME = "Parcours Entrepreneur 101";

export const HOME_FORMULAS = ["complet", "carte"] as const;
export type HomeFormula = (typeof HOME_FORMULAS)[number];

interface CourseCopy {
	title: string;
	short: string;
	audience: string;
	objective: string;
	modules: string[];
}

export interface E101Course {
	/**
	 * The live training this course is, when there is one: its card links to
	 * that landing page and an "à la carte" registration is filed under it.
	 * Null for a course not launched as a training yet.
	 */
	slug: string | null;
	hours: number;
	fr: CourseCopy;
	ar: CourseCopy;
}

export const E101_COURSES: E101Course[] = [
	{
		slug: "creating-entreprise-08-26",
		hours: 16,
		fr: {
			title: "Créer et Développer son Entreprise en Algérie",
			short: "Transformer une idée en entreprise structurée et durable.",
			audience:
				"Porteurs de projets, créateurs d'entreprise, entrepreneurs, dirigeants de PME et responsables du développement.",
			objective:
				"Transformer une idée ou un projet en entreprise structurée, compétitive et capable d'un développement durable dans le contexte économique algérien.",
			modules: [
				"Esprit entrepreneurial",
				"Opportunité et marché",
				"Business Model Canvas",
				"Business plan",
				"Structuration juridique et fiscale",
				"Marketing et commercial",
				"Pilotage et KPI",
				"Leadership du dirigeant",
				"Croissance et pérennité",
			],
		},
		ar: {
			title: "إنشاء وتطوير مؤسستك في الجزائر",
			short: "تحويل فكرة إلى مؤسسة مهيكلة ومستدامة.",
			audience:
				"حاملو المشاريع، منشئو المؤسسات، رواد الأعمال، مسيرو المؤسسات الصغيرة والمتوسطة ومسؤولو التطوير.",
			objective:
				"تحويل فكرة أو مشروع إلى مؤسسة مهيكلة وتنافسية وقادرة على التطور المستدام في السياق الاقتصادي الجزائري.",
			modules: [
				"روح المقاولاتية",
				"الفرصة والسوق",
				"نموذج الأعمال (BMC)",
				"مخطط الأعمال",
				"الهيكلة القانونية والجبائية",
				"التسويق والتجارة",
				"القيادة ومؤشرات الأداء",
				"قيادة المسيّر",
				"النمو والاستدامة",
			],
		},
	},
	{
		slug: "leadership-et-performance-manageriale",
		hours: 16,
		fr: {
			title: "Leadership et Performance Managériale",
			short: "Inspirer, mobiliser et piloter la performance de vos équipes.",
			audience:
				"Managers, chefs de service, responsables d'équipe, cadres, futurs managers et responsables RH.",
			objective:
				"Former des managers capables d'inspirer et de mobiliser leurs équipes, de piloter la performance et d'accompagner le changement, en combinant management stratégique, neurosciences appliquées et intelligence émotionnelle.",
			modules: [
				"Leader vs manager",
				"Leadership personnel",
				"Communication managériale",
				"Motivation des équipes",
				"Intelligence émotionnelle",
				"Gestion des conflits",
				"Pilotage de la performance",
				"Conduite du changement",
				"Leadership durable",
			],
		},
		ar: {
			title: "القيادة والأداء الإداري",
			short: "إلهام الفرق وتعبئتها وقيادة الأداء.",
			audience:
				"المديرون، رؤساء المصالح، مسؤولو الفرق، الإطارات، المديرون المستقبليون ومسؤولو الموارد البشرية.",
			objective:
				"تكوين مسيرين قادرين على إلهام فرقهم وتعبئتها، وقيادة الأداء ومرافقة التغيير، بالجمع بين الإدارة الاستراتيجية وعلوم الأعصاب التطبيقية والذكاء العاطفي.",
			modules: [
				"القائد مقابل المدير",
				"القيادة الشخصية",
				"التواصل الإداري",
				"تحفيز الفرق",
				"الذكاء العاطفي",
				"إدارة النزاعات",
				"قيادة الأداء",
				"قيادة التغيير",
				"القيادة المستدامة",
			],
		},
	},
	{
		slug: null,
		hours: 16,
		fr: {
			title: "SCF, Fiscalité & Finance Algérienne",
			short: "Maîtriser le SCF, la fiscalité et la gestion budgétaire.",
			audience:
				"Directeurs administratifs et financiers, cadres financiers, comptables et conseillers fiscaux.",
			objective:
				"Maîtriser le Système Comptable Financier, les mécanismes fiscaux et la gestion budgétaire conformément à la réglementation et aux lois de finances en vigueur jusqu'en 2026.",
			modules: [
				"SCF et cycle comptable",
				"IBS, IRG, TVA, IFU",
				"Jibaya'tic et G50",
				"Gestion budgétaire",
				"Trésorerie et BFR",
				"Modélisation financière",
				"Financement bancaire et CGCI",
				"Tableaux de bord financiers",
			],
		},
		ar: {
			title: "النظام المحاسبي المالي، الجباية والمالية الجزائرية",
			short: "إتقان النظام المحاسبي المالي والجباية والتسيير الميزانياتي.",
			audience:
				"المديرون الإداريون والماليون، الإطارات المالية، المحاسبون والمستشارون الجبائيون.",
			objective:
				"إتقان النظام المحاسبي المالي، الآليات الجبائية والتسيير الميزانياتي وفقاً للتنظيم وقوانين المالية السارية حتى 2026.",
			modules: [
				"النظام المحاسبي والدورة المحاسبية",
				"IBS، IRG، TVA، IFU",
				"Jibaya'tic وG50",
				"التسيير الميزانياتي",
				"الخزينة واحتياج رأس المال العامل",
				"النمذجة المالية",
				"التمويل البنكي وCGCI",
				"لوحات القيادة المالية",
			],
		},
	},
	{
		// "Comptabilité & Gestion (4 en 1)" is pitched at non-accountants too.
		slug: "comptabilite-gestion-4en1",
		hours: 14,
		fr: {
			title: "Comptabilité pour les Non-Comptables",
			short: "Lire les chiffres et décider en connaissance de cause.",
			audience:
				"Directeurs généraux, chefs de département, entrepreneurs et actionnaires.",
			objective:
				"Rendre la finance d'entreprise lisible aux dirigeants non financiers pour lire les chiffres et prendre des décisions éclairées.",
			modules: [
				"États financiers simplifiés",
				"Cycle de conversion de trésorerie",
				"Marges et seuil de rentabilité",
				"Dettes et financement",
				"Analyse des écarts",
				"Optimisation fiscale légale",
				"Communication financière",
			],
		},
		ar: {
			title: "المحاسبة لغير المحاسبين",
			short: "قراءة الأرقام واتخاذ قرارات مدروسة.",
			audience: "المديرون العامون، رؤساء الأقسام، رواد الأعمال والمساهمون.",
			objective:
				"جعل مالية المؤسسة مفهومة للمسيرين غير الماليين لقراءة الأرقام واتخاذ قرارات مستنيرة.",
			modules: [
				"القوائم المالية المبسطة",
				"دورة تحويل الخزينة",
				"الهوامش وعتبة المردودية",
				"الديون والتمويل",
				"تحليل الفوارق",
				"التحسين الجبائي القانوني",
				"التواصل المالي",
			],
		},
	},
	{
		slug: "hr-fundamentals",
		hours: 16,
		fr: {
			title: "Les Essentiels RH : Fondamentaux de la GRH",
			short: "Structurer et sécuriser la fonction RH.",
			audience:
				"Dirigeants de PME, fondateurs de startups, gestionnaires et assistants RH, office managers et managers de terrain.",
			objective:
				"Structurer et sécuriser la gestion des ressources humaines en maîtrisant les processus clés, les obligations légales et les outils de pilotage d'une fonction RH efficace.",
			modules: [
				"Fonction RH",
				"Recrutement",
				"Intégration et contrats",
				"Droit du travail",
				"Administration du personnel",
				"Temps de travail et discipline",
				"Paie",
				"CNAS et protection sociale",
				"Gestion de la performance",
				"Plan de formation",
				"Pilotage RH",
				"Digitalisation et conformité",
			],
		},
		ar: {
			title: "أساسيات تسيير الموارد البشرية",
			short: "هيكلة وتأمين وظيفة الموارد البشرية.",
			audience:
				"مسيرو المؤسسات الصغيرة والمتوسطة، مؤسسو الشركات الناشئة، مسيرو ومساعدو الموارد البشرية، مديرو المكاتب والمديرون الميدانيون.",
			objective:
				"هيكلة وتأمين تسيير الموارد البشرية من خلال إتقان العمليات الأساسية، الالتزامات القانونية وأدوات قيادة وظيفة موارد بشرية فعالة.",
			modules: [
				"وظيفة الموارد البشرية",
				"التوظيف",
				"الإدماج والعقود",
				"قانون العمل",
				"إدارة المستخدمين",
				"وقت العمل والانضباط",
				"الأجور",
				"CNAS والحماية الاجتماعية",
				"تسيير الأداء",
				"مخطط التكوين",
				"قيادة الموارد البشرية",
				"الرقمنة والامتثال",
			],
		},
	},
	{
		// The live sales-techniques training (14 h, like this course).
		slug: "performance-commerciale-et-developpement-des-ventes",
		hours: 14,
		fr: {
			title: "Techniques Commerciales & Culture Algérienne",
			short: "Vendre et négocier sur le marché algérien.",
			audience:
				"Directeurs commerciaux, ingénieurs d'affaires, commerciaux terrain et responsables grands comptes.",
			objective:
				"Maîtriser le cycle de vente et les techniques de négociation en s'adaptant à la psychologie de l'acheteur algérien et aux spécificités du marché.",
			modules: [
				"Stratégie commerciale",
				"Prospection B2B et B2C",
				"Vente relationnelle",
				"Négociation et closing",
				"Vente au secteur public",
				"E-commerce et paiement à la livraison",
				"CRM",
				"Management de la force de vente",
			],
		},
		ar: {
			title: "التقنيات التجارية والثقافة الجزائرية",
			short: "البيع والتفاوض في السوق الجزائرية.",
			audience:
				"المديرون التجاريون، مهندسو الأعمال، التجاريون الميدانيون ومسؤولو الحسابات الكبرى.",
			objective:
				"إتقان دورة البيع وتقنيات التفاوض مع التكيف مع نفسية المشتري الجزائري وخصوصيات السوق.",
			modules: [
				"الاستراتيجية التجارية",
				"التنقيب B2B وB2C",
				"البيع العلائقي",
				"التفاوض والإغلاق",
				"البيع للقطاع العمومي",
				"التجارة الإلكترونية والدفع عند التسليم",
				"CRM",
				"إدارة القوة البيعية",
			],
		},
	},
	{
		slug: null,
		hours: 14,
		fr: {
			title: "Marketing Digital : Algérie 2026",
			short: "Des campagnes digitales mesurables, adaptées à l'Algérie.",
			audience:
				"Responsables marketing, chefs de projets digitaux et créateurs de contenu.",
			objective:
				"Déployer des campagnes digitales efficaces et mesurables, adaptées au comportement de l'internaute algérien et aux contraintes du paiement électronique.",
			modules: [
				"Stratégie digitale",
				"SEO local",
				"Réseaux sociaux",
				"Contenu et vidéo",
				"Publicité payante",
				"E-mailing",
				"Marketing d'influence",
				"Social commerce et WhatsApp Business",
				"IA générative",
				"Data et GA4",
			],
		},
		ar: {
			title: "التسويق الرقمي: الجزائر 2026",
			short: "حملات رقمية قابلة للقياس ومكيّفة مع الجزائر.",
			audience: "مسؤولو التسويق، رؤساء المشاريع الرقمية وصنّاع المحتوى.",
			objective:
				"نشر حملات رقمية فعالة وقابلة للقياس، مكيّفة مع سلوك مستخدم الإنترنت الجزائري وقيود الدفع الإلكتروني.",
			modules: [
				"الاستراتيجية الرقمية",
				"SEO المحلي",
				"شبكات التواصل",
				"المحتوى والفيديو",
				"الإعلانات المدفوعة",
				"البريد الإلكتروني",
				"تسويق المؤثرين",
				"التجارة الاجتماعية وWhatsApp Business",
				"الذكاء الاصطناعي التوليدي",
				"البيانات وGA4",
			],
		},
	},
	{
		slug: null,
		hours: 14,
		fr: {
			title: "Scaling & Croissance en Algérie",
			short: "Passer de la PME à la grande structure.",
			audience:
				"Fondateurs, directeurs généraux et exécutifs, directeurs du développement.",
			objective:
				"Donner les outils stratégiques et opérationnels pour passer de la PME à la grande structure, ou préparer l'expansion internationale.",
			modules: [
				"Stratégies de croissance",
				"Financement de l'expansion",
				"Industrialisation et ERP",
				"Expansion géographique",
				"Import et risque de change",
				"Gouvernance",
				"Fusions-acquisitions",
				"ESG",
				"Cybersécurité",
			],
		},
		ar: {
			title: "التوسع والنمو في الجزائر",
			short: "الانتقال من مؤسسة صغيرة إلى هيكل كبير.",
			audience: "المؤسسون، المديرون العامون والتنفيذيون، مديرو التطوير.",
			objective:
				"توفير الأدوات الاستراتيجية والعملياتية للانتقال من المؤسسة الصغيرة والمتوسطة إلى الهيكل الكبير، أو التحضير للتوسع الدولي.",
			modules: [
				"استراتيجيات النمو",
				"تمويل التوسع",
				"التصنيع وERP",
				"التوسع الجغرافي",
				"الاستيراد ومخاطر الصرف",
				"الحوكمة",
				"الاندماج والاستحواذ",
				"ESG",
				"الأمن السيبراني",
			],
		},
	},
	{
		slug: "levee-de-fonds",
		hours: 16,
		fr: {
			title: "Levée de Fonds & IPO à la Bourse d'Alger",
			short: "De la levée de fonds à l'IPO à la Bourse d'Alger.",
			audience:
				"Dirigeants de startups en croissance, actionnaires majoritaires et directeurs financiers.",
			objective:
				"Maîtriser le parcours complexe de l'ouverture du capital, de la levée de fonds privée à l'introduction en Bourse (IPO) à la Bourse d'Alger.",
			modules: [
				"Levée de fonds et term sheet",
				"Valorisation : DCF et multiples",
				"Label startup et IPO",
				"Marché financier",
				"COSOB et intermédiaires",
				"Gouvernance d'entreprise",
				"Due diligence",
				"Notice d'information",
				"Processus d'IPO",
				"Vie post-IPO",
				"Stratégies de sortie",
			],
		},
		ar: {
			title: "جمع التمويل والطرح العام الأولي في بورصة الجزائر",
			short: "من جمع التمويل إلى الطرح العام في بورصة الجزائر.",
			audience:
				"مسيرو الشركات الناشئة في طور النمو، المساهمون الرئيسيون والمديرون الماليون.",
			objective:
				"إتقان المسار المعقد لفتح رأس المال، من جمع التمويل الخاص إلى الطرح العام الأولي (IPO) في بورصة الجزائر.",
			modules: [
				"جمع التمويل وورقة الشروط",
				"التقييم: DCF والمضاعفات",
				"علامة Startup والطرح العام",
				"السوق المالية",
				"COSOB والوسطاء",
				"حوكمة المؤسسات",
				"العناية الواجبة",
				"مذكرة الإعلام",
				"مسار الطرح العام",
				"ما بعد الطرح",
				"استراتيجيات الخروج",
			],
		},
	},
];

export const E101_FAQ: Record<E101Lang, { q: string; a: string }[]> = {
	fr: [
		{
			q: "Faut-il suivre les formations dans l'ordre ?",
			a: "C'est recommandé, mais chaque formation reste accessible à la carte.",
		},
		{
			q: "Quelle est la durée du parcours ?",
			a: "136 heures : 9 formations de 14 à 16 heures.",
		},
		{
			q: "Les contenus sont-ils adaptés à l'Algérie ?",
			a: "Oui : SCF, lois de finances 2026, Jibaya'tic, CNAS, COSOB, Bourse d'Alger.",
		},
		{
			q: "Puis-je inscrire mon équipe ?",
			a: "Oui, sur devis, avec des sessions dédiées.",
		},
	],
	ar: [
		{
			q: "هل يجب متابعة التكوينات بالترتيب؟",
			a: "يُنصح بذلك، لكن كل تكوين يبقى متاحاً بشكل منفصل.",
		},
		{ q: "ما مدة المسار؟", a: "136 ساعة: 9 تكوينات من 14 إلى 16 ساعة." },
		{
			q: "هل المحتوى مكيّف مع الجزائر؟",
			a: "نعم: النظام المحاسبي المالي، قوانين المالية 2026، Jibaya'tic، CNAS، COSOB، بورصة الجزائر.",
		},
		{
			q: "هل يمكنني تسجيل فريقي؟",
			a: "نعم، بعرض سعر مخصص وحصص خاصة.",
		},
	],
};

const fr = {
	metaTitle:
		"Carte Entrepreneur 101 — De l'idée à la Bourse d'Alger | Moustachir Academy",
	metaDescription:
		"Parcours Entrepreneur 101 : 9 formations (136 h) pour porteurs de projets et dirigeants de PME en Algérie, de la création d'entreprise à la levée de fonds et l'IPO à la Bourse d'Alger.",
	signup: "S'inscrire",
	eyebrow1: "Parcours Entrepreneur 101",
	eyebrow2: "136 h · 9 formations · 1 carte",
	h1a: "De l'idée à la Bourse d'Alger.",
	h1b: "Neuf cachets, un visa.",
	heroP1: "9 formations pour porteurs de projets et dirigeants de PME.",
	heroP2: "Chaque formation validée = un cachet sur votre carte.",
	ctaJoin: "Rejoindre le parcours",
	ctaSee: "Voir les 9 formations",
	namePh: "Écrivez votre nom sur la carte",
	tipDesk: "Glissez, cliquez ou utilisez les flèches pour retourner la carte",
	tipTouch: "Touchez ou glissez la carte pour la retourner",
	progLabel: "Le programme",
	progH2: "De l'idée à l'introduction en Bourse, dans le contexte algérien.",
	progP:
		"Commercial, finance, RH et stratégie : des compétences pratiques, jusqu'à la levée de fonds et l'IPO à la Bourse d'Alger.",
	h: "h",
	stat1: "de formation intensive",
	stat2: "formations, de la création à l'IPO",
	stat3: "carte, 9 cachets à collecter",
	pubLabel: "À qui s'adresse ce parcours",
	pubH2: "Pour celles et ceux qui font grandir une entreprise en Algérie.",
	pub1: "Porteurs de projets et fondateurs",
	pub1d: "Structurer une idée ou une première activité.",
	pub2: "Dirigeants de PME",
	pub2d: "Professionnaliser la gestion et le pilotage.",
	pub3: "Cadres finance, RH et commerciaux",
	pub3d: "Des méthodes adaptées au marché algérien.",
	pub4: "Startups en croissance",
	pub4d: "Préparer une levée de fonds ou une IPO.",
	formLabel: "Les 9 formations",
	formH2: "Faites défiler : chaque formation appose son cachet.",
	stamps: "cachets",
	stamp: "cachet",
	flip: "Retourner",
	doneTitle: "Carte complète.",
	doneDesc: "Les 9 cachets sont réunis. Il ne reste qu'à réserver votre place.",
	reserve: "Réserver",
	stamped: "Cachet apposé",
	upcoming: "Cachet à venir",
	fullProgram: "Programme complet",
	themes: "thèmes",
	public: "Public",
	scrollHint: "Faites défiler pour collecter vos cachets",
	viewTraining: "Voir la formation",
	enrollThis: "S'inscrire à cette formation",
	visaLabel: "La Carte Entrepreneur 101",
	visaH2: "cachets. Un visa pour la suite de votre parcours.",
	visaAlready: "Déjà",
	visaCollected: "cachets collectés sur cette page.",
	visaCollected2: "cachets collectés",
	visa1: "Un cachet par formation validée",
	visa1d: "Avec une attestation à la clé.",
	visa2: "Carte complète, certification Entrepreneur 101",
	visa2d: "Signée Moustachir Academy.",
	visa3: "Votre visa dans l'écosystème Moustachir",
	visa3d: "Consultants, Moustachir AI et communauté certifiée.",
	priceLabel: "Tarifs",
	priceH2: "À la carte, ou le parcours complet pour remplir la carte.",
	p1Title: "À la carte",
	p1Sub: "Une formation, un cachet.",
	p1Unit: "DA / formation",
	p1a: "14 à 16 heures de formation",
	p1b: "Supports et cas pratiques algériens",
	p1c: "Attestation et cachet sur la carte",
	p1Cta: "Choisir une formation",
	algeria: "Algérie",
	recommended: "Recommandé",
	p2Title: "Parcours complet",
	p2Sub: "9 formations, 9 cachets, la carte certifiée.",
	da: "DA",
	p2a: "136 heures · soit 56 000 DA d'économie",
	p2b: "Accompagnement entre les formations",
	p2c: "Certification Entrepreneur 101 et accès à l'écosystème Moustachir",
	p2Cta: "Rejoindre le parcours complet",
	priceNote:
		"Tarifs indicatifs hors taxes · devis sur mesure pour les entreprises et les groupes.",
	regLabel: "Inscription",
	regH2: "Réservez votre place et recevez votre Carte Entrepreneur 101.",
	regP: "Un conseiller vous rappelle sous 48 h.",
	fName: "Nom et prénom",
	fNamePh: "Rania Benali",
	fCompany: "Entreprise",
	fCompanyPh: "Nom de votre société",
	fEmail: "E-mail",
	fPhone: "Téléphone",
	optional: "Facultatif",
	plan: "Formule",
	planFull: "Parcours complet · 9 formations",
	planCarte: "À la carte",
	fCourse: "Formation choisie",
	fCoursePh: "Choisissez une formation",
	submit: "Réserver ma place",
	sending: "Envoi…",
	submitNote: "Sans engagement · réponse sous 48 h",
	sentTitle: "Demande reçue.",
	sentDesc:
		"Un conseiller Moustachir Academy vous rappelle sous 48 h pour finaliser votre inscription et vous remettre votre Carte Entrepreneur 101.",
	errName: "Indiquez votre nom et prénom.",
	errPhone: "Indiquez un numéro de téléphone algérien valide.",
	errEmail: "Indiquez une adresse e-mail valide.",
	errCourse: "Choisissez la formation qui vous intéresse.",
	errGeneric: "L'envoi a échoué. Réessayez ou écrivez-nous.",
	faqLabel: "Questions fréquentes",
	faqH2: "Ce que les futurs participants nous demandent.",
	footDesc:
		"Moustachir Academy — les formations de la plateforme de conseil Moustachir, pensées pour les entrepreneurs algériens.",
	contact: "Contact",
	footParcours: "Parcours",
	rights: "Tous droits réservés.",
	cardAria:
		"Carte Entrepreneur 101 · {n} cachets sur 9 · Entrée pour retourner",
	nav: [
		["programme", "Programme"],
		["formations", "9 formations"],
		["visa", "La carte"],
		["tarifs", "Tarifs"],
		["faq", "FAQ"],
	] as [string, string][],
};

export type E101Copy = typeof fr;

const ar: E101Copy = {
	metaTitle:
		"بطاقة Entrepreneur 101 — من الفكرة إلى بورصة الجزائر | أكاديمية مستشير",
	metaDescription:
		"مسار Entrepreneur 101: تسعة تكوينات (136 ساعة) لحاملي المشاريع ومسيري المؤسسات الصغيرة والمتوسطة في الجزائر، من إنشاء المؤسسة إلى جمع التمويل والطرح العام في بورصة الجزائر.",
	signup: "سجّل الآن",
	eyebrow1: "مسار Entrepreneur 101",
	eyebrow2: "136 ساعة · 9 تكوينات · بطاقة واحدة",
	h1a: "من الفكرة إلى بورصة الجزائر.",
	h1b: "تسعة أختام، تأشيرة واحدة.",
	heroP1: "9 تكوينات لحاملي المشاريع ومسيري المؤسسات الصغيرة والمتوسطة.",
	heroP2: "كل تكوين تجتازه = ختم على بطاقتك.",
	ctaJoin: "انضم إلى المسار",
	ctaSee: "اكتشف التكوينات التسعة",
	namePh: "اكتب اسمك على البطاقة",
	tipDesk: "اسحب البطاقة أو انقر عليها أو استعمل الأسهم لقلبها",
	tipTouch: "المس البطاقة أو اسحبها لقلبها",
	progLabel: "البرنامج",
	progH2: "من الفكرة إلى الطرح في البورصة، في السياق الجزائري.",
	progP:
		"التجارة، المالية، الموارد البشرية والاستراتيجية: كفاءات عملية وصولاً إلى جمع التمويل والطرح العام في بورصة الجزائر.",
	h: "سا",
	stat1: "من التكوين المكثف",
	stat2: "تكوينات، من الإنشاء إلى الطرح العام",
	stat3: "بطاقة، 9 أختام لجمعها",
	pubLabel: "لمن هذا المسار",
	pubH2: "لكل من يبني مؤسسة في الجزائر ويريد لها أن تكبر.",
	pub1: "حاملو المشاريع والمؤسسون",
	pub1d: "هيكلة فكرة أو نشاط أول.",
	pub2: "مسيرو المؤسسات الصغيرة والمتوسطة",
	pub2d: "احتراف التسيير والقيادة بالأرقام.",
	pub3: "إطارات المالية والموارد البشرية والتجارة",
	pub3d: "مناهج مكيّفة مع السوق الجزائرية.",
	pub4: "الشركات الناشئة في طور النمو",
	pub4d: "التحضير لجمع التمويل أو الطرح العام.",
	formLabel: "التكوينات التسعة",
	formH2: "مرّر للأسفل: كل تكوين يضع ختمه على البطاقة.",
	stamps: "أختام",
	stamp: "ختم",
	flip: "اقلب البطاقة",
	doneTitle: "البطاقة مكتملة.",
	doneDesc: "جمعت الأختام التسعة. لم يبق سوى حجز مكانك.",
	reserve: "احجز",
	stamped: "تم الختم",
	upcoming: "ختم قادم",
	fullProgram: "البرنامج الكامل",
	themes: "محاور",
	public: "الفئة المستهدفة",
	scrollHint: "مرّر للأسفل لجمع أختامك",
	viewTraining: "صفحة التكوين",
	enrollThis: "سجّل في هذا التكوين",
	visaLabel: "بطاقة Entrepreneur 101",
	visaH2: "أختام. تأشيرتك لما بعد المسار.",
	visaAlready: "جمعت حتى الآن",
	visaCollected: "أختام في هذه الصفحة.",
	visaCollected2: "أختام",
	visa1: "ختم عن كل تكوين تجتازه",
	visa1d: "مع شهادة مشاركة.",
	visa2: "بطاقة مكتملة، شهادة Entrepreneur 101",
	visa2d: "موقّعة من Moustachir Academy.",
	visa3: "تأشيرتك في منظومة Moustachir",
	visa3d: "المستشارون، Moustachir AI ومجتمع المعتمدين.",
	priceLabel: "الأسعار",
	priceH2: "تكوين واحد، أو المسار الكامل لملء البطاقة.",
	p1Title: "تكوين واحد",
	p1Sub: "تكوين واحد، ختم واحد.",
	p1Unit: "دج / تكوين",
	p1a: "من 14 إلى 16 ساعة تكوين",
	p1b: "دعائم وحالات عملية جزائرية",
	p1c: "شهادة وختم على البطاقة",
	p1Cta: "اختر تكويناً",
	algeria: "الجزائر",
	recommended: "موصى به",
	p2Title: "المسار الكامل",
	p2Sub: "9 تكوينات، 9 أختام، بطاقة معتمدة.",
	da: "دج",
	p2a: "136 ساعة · أي توفير 56 000 دج",
	p2b: "مرافقة بين التكوينات",
	p2c: "شهادة Entrepreneur 101 والولوج إلى منظومة Moustachir",
	p2Cta: "انضم إلى المسار الكامل",
	priceNote: "أسعار استرشادية خارج الرسوم · عروض مخصصة للمؤسسات والمجموعات.",
	regLabel: "التسجيل",
	regH2: "احجز مكانك واستلم بطاقة Entrepreneur 101.",
	regP: "يتصل بك مستشار خلال 48 ساعة.",
	fName: "الاسم واللقب",
	fNamePh: "رانية بن علي",
	fCompany: "المؤسسة",
	fCompanyPh: "اسم شركتك",
	fEmail: "البريد الإلكتروني",
	fPhone: "الهاتف",
	optional: "اختياري",
	plan: "الصيغة",
	planFull: "المسار الكامل · 9 تكوينات",
	planCarte: "تكوين واحد",
	fCourse: "التكوين المختار",
	fCoursePh: "اختر تكويناً",
	submit: "احجز مكاني",
	sending: "جارٍ الإرسال…",
	submitNote: "دون التزام · رد خلال 48 ساعة",
	sentTitle: "تم استلام طلبك.",
	sentDesc:
		"يتصل بك مستشار من Moustachir Academy خلال 48 ساعة لإتمام تسجيلك وتسليمك بطاقة Entrepreneur 101.",
	errName: "أدخل الاسم واللقب.",
	errPhone: "أدخل رقم هاتف جزائري صالح.",
	errEmail: "أدخل بريداً إلكترونياً صالحاً.",
	errCourse: "اختر التكوين الذي يهمك.",
	errGeneric: "تعذّر الإرسال. حاول مجدداً أو راسلنا.",
	faqLabel: "أسئلة شائعة",
	faqH2: "ما يسألنا عنه المشاركون المستقبليون.",
	footDesc:
		"Moustachir Academy — تكوينات منصة الاستشارة Moustachir، مصممة لرواد الأعمال الجزائريين.",
	contact: "اتصل بنا",
	footParcours: "المسار",
	rights: "جميع الحقوق محفوظة.",
	cardAria: "بطاقة Entrepreneur 101 · {n} أختام من 9 · اضغط Enter لقلبها",
	nav: [
		["programme", "البرنامج"],
		["formations", "التكوينات"],
		["visa", "البطاقة"],
		["tarifs", "الأسعار"],
		["faq", "أسئلة"],
	],
};

export const E101_COPY: Record<E101Lang, E101Copy> = { fr, ar };

/** The page has French and Arabic only; any other site locale reads French. */
export function e101Lang(locale: string): E101Lang {
	return locale === "ar" ? "ar" : "fr";
}

/** "01" … "09". */
export function courseNumber(index: number) {
	return String(index + 1).padStart(2, "0");
}

/* -------------------------------------------------------------------------- */
/*  Home-page registrations                                                   */
/* -------------------------------------------------------------------------- */

export const homeLeadInputSchema = z
	.object({
		fullName: z.string().trim().min(2).max(120),
		phone: z
			.string()
			.trim()
			.min(9)
			.max(20)
			.regex(/^[0-9+\s]+$/, "رقم هاتف غير صالح"),
		email: z.string().trim().max(160).email(),
		company: z.string().trim().max(120).optional(),
		formula: z.enum(HOME_FORMULAS),
		/** 1-based course number; required "à la carte", ignored otherwise. */
		course: z.number().int().min(1).max(E101_COURSES.length).optional(),
		utmSource: z.string().max(120).optional(),
		utmMedium: z.string().max(120).optional(),
		utmCampaign: z.string().max(120).optional(),
		// honeypot — real users never fill this hidden field
		website: z.string().max(200).optional(),
	})
	.refine(
		(input) => input.formula === "complet" || input.course !== undefined,
		{
			message: "اختر التكوين",
			path: ["course"],
		},
	);

export type HomeLeadInput = z.infer<typeof homeLeadInputSchema>;

/** Sales filter the orders page by source, so each formula gets its own. */
export function homeLeadSource(formula: HomeFormula) {
	return formula === "complet" ? "home-e101-full" : "home-e101-single";
}

/**
 * The training an "à la carte" registration belongs to, when the chosen course
 * is a live one. Null means "file it under the path" — the full path, or a
 * course not launched yet. The caller still checks the training is active.
 */
export function homeLeadTrainingSlug(
	formula: HomeFormula,
	course: number | undefined,
) {
	if (formula !== "carte" || course === undefined) return null;
	return E101_COURSES[course - 1]?.slug ?? null;
}

/**
 * What the form said beyond name, phone and e-mail, for the order window — in
 * Arabic, like the rest of the admin. The course stays named even when the
 * lead lands under that course's own training, so the note reads the same
 * wherever it is filed.
 */
export function homeLeadNotes(
	input: Pick<HomeLeadInput, "formula" | "course" | "company">,
) {
	const lines = ["طلب من الصفحة الرئيسية — بطاقة Entrepreneur 101"];
	if (input.formula === "complet") {
		lines.push("الصيغة: المسار الكامل (9 تكوينات)");
	} else {
		const course =
			input.course === undefined ? undefined : E101_COURSES[input.course - 1];
		lines.push(
			course && input.course !== undefined
				? `الصيغة: تكوين واحد — ${courseNumber(input.course - 1)} · ${course.ar.title}`
				: "الصيغة: تكوين واحد",
		);
	}
	const company = input.company?.trim();
	if (company) lines.push(`المؤسسة: ${company}`);
	return lines.join("\n");
}
